import { RELATIONS } from '../pedigree-utils';

/**
 * What the selection-mode banner says while a "Link to Existing…" is in flight.
 *
 * The old text — "Entered Selection Mode: click a target node to complete Set
 * Relation. Press Esc or Cancel to exit." — had two problems. It named a menu item
 * that no longer exists, and it said nothing about DIRECTION, which is the part
 * people actually find hard.
 *
 * Direction matters most for a child link. `linkChildRelation(members, childPid,
 * parentPid)` is called with the RIGHT-CLICKED person as the child and the person you
 * click next as the parent — so choosing "Child" does not add a child to the selected
 * person, it records the selected person AS a child, and the node you go on to click
 * is their parent. Nothing on screen said so, which is why the feature felt like a
 * guess.
 *
 * Each line therefore names three things: who is being linked, the role they take,
 * and who to click. Kept to one short sentence plus the key hint — the banner spans
 * the canvas, and a long one covers the pedigree it is asking you to click.
 *
 * The person is named as "UPN 26 - John Smith", matching the twin-status card and the
 * rest of the sidebar: the UPN is what a clinician cross-references, and on a family
 * where several relatives share a surname the name alone identifies nobody.
 *
 * A pure function so it can be asserted directly; ChartBox has enough props already.
 *
 * @param {?{node: object, relation: string}} pending the armed relation, or null
 */
export const selectionPromptFor = (pending) => {
  const person = pending?.node;
  const upn = person?.upn ?? person?.pid;
  const name = [person?.firstName, person?.lastName].filter(Boolean).join(' ');
  const who =
    upn != null
      ? `UPN ${upn}${name ? ` - ${name}` : ''}`
      : name || 'this person';

  const byRelation = {
    [RELATIONS.partner]: `Linking ${who} as a spouse — click their partner.`,
    [RELATIONS.siblings]: `Linking ${who} as a sibling — click their sibling.`,
    [RELATIONS.children]: `Linking ${who} as a child — click their parent.`
  };

  return `${
    byRelation[pending?.relation] ||
    `Linking ${who} — click the person to link them to.`
  } Esc to cancel.`;
};
